import { watch, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { showSessionWarning, closeLoader, showAlert } from '@/utils/sweetAlert';
import { useAuthStore } from '@/stores/authStore';
import { LOGOUT_BROADCAST_KEY, broadcastLogout } from '@/utils/sessionBroadcast';
import { getSessionCookie, setSessionCookie } from '@/utils/sessionCookie';
import axios from '@/axios';

const ACTIVITY_KEY = 'keru_last_activity';
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'scroll', 'touchstart', 'click'];

export function useIdleTimeout(options = {}) {

    const idleMs = options.idleMs ?? 30 * 60 * 1000;
    const warningMs = options.warningMs ?? 60 * 1000;
    const checkMs = options.checkMs ?? 5000;
    const writeThrottleMs = 2000;

    const authStore = useAuthStore();
    const route = useRoute();

    let lastActivity = Date.now();
    let lastWrite = 0;
    let checkTimer = null;
    let warningOpen = false;
    let running = false;
    let cookieWorks = false;
    let sessionEnding = false;

    function recordActivity() {

        const timestamp = Date.now();
        lastActivity = timestamp;

        if (timestamp - lastWrite > writeThrottleMs) {

            lastWrite = timestamp;

            try {
                localStorage.setItem(ACTIVITY_KEY, String(timestamp));
            } catch (error) {
                lastWrite = 0;
            }
        }
    }

    function readSharedActivity() {

        try {

            const stored = Number(localStorage.getItem(ACTIVITY_KEY));

            if (stored && stored > lastActivity) {
                lastActivity = stored;
            }

        } catch (error) {
            return lastActivity;
        }

        return lastActivity;
    }

    async function endSession(reason) {

        if (sessionEnding) return;
        sessionEnding = true;

        stop();

        const message = reason === 'superseded'
            ? 'Se cerró esta sesión porque iniciaste sesión en otra ventana.'
            : 'Tu sesión se cerró porque cerraste sesión en otra ventana.';

        await showAlert({ icon: 'info', title: 'Sesión finalizada', message });

        authStore.clearAuthData(false);

        window.location.href = process.env.VUE_APP_FRONTEND_URL;
    }

    function checkSharedSession() {

        if (!cookieWorks || !authStore.accessToken) {
            return false;
        }

        const cookie = getSessionCookie();

        if (!cookie) {
            endSession('loggedOut');
            return true;
        }

        if (cookie.token && cookie.token !== authStore.accessToken) {
            endSession('superseded');
            return true;
        }

        return false;
    }

    async function performLogout(broadcast = true) {

        stop();

        try {

            await axios.post('auth/logout', {}, {
                headers: authStore.accessToken ? { Authorization: `Bearer ${authStore.accessToken}` } : {}
            });

        } catch (error) {
            console.error('Idle logout error:', error);
        }

        authStore.clearAuthData();

        if (broadcast) {
            broadcastLogout();
        }

        window.location.href = process.env.VUE_APP_FRONTEND_URL;
    }

    async function showWarning() {

        if (warningOpen) return;
        warningOpen = true;

        const result = await showSessionWarning(warningMs);

        warningOpen = false;

        if (result.isConfirmed) {
            recordActivity();
            return;
        }

        if (result.dismiss === 'timer' || result.dismiss === 'cancel') {
            performLogout();
        }
    }

    function tick() {

        if (!route.meta.requiresAuth) return;

        if (!authStore.accessToken && !authStore.authenticated) return;

        if (checkSharedSession()) return;

        const idleFor = Date.now() - readSharedActivity();

        if (idleFor >= idleMs && !warningOpen) {
            showWarning();
        }
    }

    function onVisibility() {

        if (document.visibilityState === 'visible') {
            checkSharedSession();
        }
    }

    function onStorage(event) {

        if (event.key === LOGOUT_BROADCAST_KEY && event.newValue) {

            endSession('loggedOut');
            return;
        }

        if (event.key === ACTIVITY_KEY && event.newValue) {

            const stored = Number(event.newValue);

            if (stored > lastActivity) {
                lastActivity = stored;
            }

            if (warningOpen && (Date.now() - lastActivity) < idleMs) {
                warningOpen = false;
                closeLoader();
            }
        }
    }

    function start() {

        if (authStore.accessToken) {

            setSessionCookie(authStore.accessToken, authStore.id);
            cookieWorks = !!getSessionCookie();
        }

        if (running) return;
        running = true;

        recordActivity();

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.addEventListener(eventName, recordActivity, { passive: true });
        });

        window.addEventListener('storage', onStorage);
        document.addEventListener('visibilitychange', onVisibility);

        checkTimer = setInterval(tick, checkMs);
    }

    function stop() {

        if (!running) return;
        running = false;

        ACTIVITY_EVENTS.forEach((eventName) => {
            window.removeEventListener(eventName, recordActivity);
        });

        window.removeEventListener('storage', onStorage);
        document.removeEventListener('visibilitychange', onVisibility);

        if (checkTimer) {
            clearInterval(checkTimer);
            checkTimer = null;
        }

        if (warningOpen) {
            warningOpen = false;
            closeLoader();
        }
    }

    onMounted(() => {
        if (authStore.accessToken || authStore.authenticated) start();
    });

    watch(
        () => authStore.accessToken || authStore.authenticated,
        (active) => {
            if (active) start();
            else stop();
        }
    );

    onBeforeUnmount(() => {
        stop();
    });

    return { start, stop };
}
